import { Board } from "../board";
import { DistanceMap } from "../distanceMap";
import type { PhysicalThing } from "../physicalThing";
import { randomInt, randomChance, shuffleArray } from "../random";
import { BlastDoor } from "../things/blastDoor";
import { CaveRock } from "../things/caveRock";
import { Chasm } from "../things/chasm";
import { MetalGridFloor } from "../things/floors/metalGridFloor";
import { LadderDown } from "../things/ladderDown";
import { LadderUp } from "../things/ladderUp";
import { LavaPool } from "../things/lavaPool";
import { LevelChangerThing } from "../things/levelChangerThing";
import { Upstairs } from "../things/upstairs";
import { WaterTile } from "../things/waterTile";
import type { LootTable } from "./lootTable";
import type { VendingTable } from "./vendingTable";
import type { MobGenerationConfig } from "./mobGenerationConfig";
import type { MobSpawnerConfig } from "../things/portal2/mobSpawnerConfig";
import type { Prefab } from "./prefab";
import { XY } from "../xy";
import { generateWithRetries, type LevelAcceptanceCriteria } from "./levelGenerator";
import { LevelGeneratorScrewUp } from "./levelGeneratorScrewUp";
import {
	breadthFirstSearch,
	getRandomEmptyCell,
	getRandomEmptyCellInArea,
	getRandomXYByCriteriaInArea,
	getSimplexNoiseArea,
	placeNxNMultiTileThing,
	placeRandomPortal,
	populateLootBoxes,
	populateVendingMachines,
	putPrefabOnArea,
	removeAllThingsAt,
	spawnMobsWithNoise,
	spawnThings,
	assignGoldToPrePlacedMobs,
} from "./levelGeneratorUtils";
import { getBigContainerPrefab } from "./prefabs/generalPrefabs";
import {
	getCaveArrival,
	getChasmLoot,
	getFireTowerChasm,
	getLavaCross,
	getLTunnel,
	getLTunnel2,
	getSculptures3,
} from "./prefabs/cavePrefabs";
import { getSmallBloodTemple } from "./prefabs/wastelandPrefabs";
import { Slugant } from "../mobs/slugant";
import { BigSquid } from "../mobs/bigSquid";
import { ArtilleryRobot } from "../things/artilleryRobot";
import { MiningRobot } from "../things/miningRobot";
import { LootBox } from "../things/lootBox";
import { MetalLootBox } from "../things/metalLootBox";
import { MedicalLootBox } from "../things/medicalLootBox";
import { KeyLootBox } from "../things/keyLootBox";
import { BOARD_WIDTH, BOARD_HEIGHT } from "../gameConfig";

const MOB_SPAWN_MIN_DISTANCE_FROM_EXIT = 4;

export interface CaveLevelParams {
	levelNumber: number;
	rng?: () => number;

	// Cave shape
	randomStartingWallPercentage?: number;
	smoothingIterationsType1?: number;
	smoothingIterationsType2?: number;
	requiredOpenAreaPercent?: { min: number; max: number };
	wallSupplier?: () => PhysicalThing;
	defaultFloor?: string;

	// Terrain features
	generateRiver?: boolean;
	generateLavaPools?: boolean;
	generateChasms?: boolean;
	chasmFrequency?: number;
	chasmLimit?: number;
	lavaFrequency?: number;
	lavaLimit?: number;

	// Entrance/exit
	generateSpecialEntrance?: boolean;
	generateExit?: boolean;
	exitLevelNumber?: number;
	previousLevelNumber?: number | null;
	minExitDistance?: number;
	entranceSupplier?: (xy: XY, destinationLevel: number) => LevelChangerThing;
	exitSupplier?: (xy: XY, destinationLevel: number) => LevelChangerThing;

	// Spawning
	numberOfPortals?: number;
	numberOfFlavourPrefabs?: number;
	numberOfBigSquids?: number;
	numberOfArtilleryRobots?: number;
	numberOfDrillerRobots?: number;
	slugantPortalChance?: number;
	reinforcedPortalChance?: number;
	portalSpawnProbability?: number | null;
	mobGenerationConfig?: MobGenerationConfig[];
	mobSpawnerConfig?: MobSpawnerConfig | null;
	mobSpawnMinDistanceFromArrival?: number;

	// Loot
	numberOfMedikits?: number;
	numberOfLootBoxes?: number;
	numberOfMetalLootBoxes?: number;
	numberOfKeys?: number;
	numberOfBigContainers?: number;
	probabilityToPlaceLootInHardToReachAreas?: number;
	lootTable?: LootTable | null;
	metalLootTable?: LootTable | null;
	vendingTable?: VendingTable | null;
	rewardLevel?: number;

	// Prefabs
	requiredPrefabs?: Prefab[];

	accept?: LevelAcceptanceCriteria;
}

const DEFAULTS: Required<Omit<CaveLevelParams, "levelNumber" | "accept">> = {
	rng: Math.random,
	randomStartingWallPercentage: 0.48,
	smoothingIterationsType1: 1,
	smoothingIterationsType2: 2,
	requiredOpenAreaPercent: { min: 0.2, max: 0.6 },
	wallSupplier: () => new CaveRock(new XY(0, 0)),
	defaultFloor: "CaveFloor",
	generateRiver: true,
	generateLavaPools: true,
	generateChasms: true,
	chasmFrequency: 1.6,
	chasmLimit: 0.76,
	lavaFrequency: 2,
	lavaLimit: 0.65,
	generateSpecialEntrance: false,
	generateExit: true,
	exitLevelNumber: 0,
	previousLevelNumber: null,
	minExitDistance: 20,
	entranceSupplier: (xy, lvl) => new LadderUp(xy, lvl),
	exitSupplier: (xy, lvl) => new LadderDown(xy, lvl),
	numberOfPortals: 0,
	numberOfFlavourPrefabs: 0,
	numberOfBigSquids: 0,
	numberOfArtilleryRobots: 0,
	numberOfDrillerRobots: 0,
	slugantPortalChance: 0,
	reinforcedPortalChance: 0,
	portalSpawnProbability: null,
	mobGenerationConfig: [],
	mobSpawnerConfig: null,
	mobSpawnMinDistanceFromArrival: 10,
	numberOfMedikits: 0,
	numberOfLootBoxes: 0,
	numberOfMetalLootBoxes: 0,
	numberOfKeys: 0,
	numberOfBigContainers: 0,
	probabilityToPlaceLootInHardToReachAreas: 0.2,
	lootTable: null,
	metalLootTable: null,
	vendingTable: null,
	rewardLevel: 0,
	requiredPrefabs: [],
};

type ResolvedParams = typeof DEFAULTS & { levelNumber: number };

export function generateCaveLevel(params: CaveLevelParams): Board {
	const p: ResolvedParams = { ...DEFAULTS, ...params };
	return generateWithRetries(p.levelNumber, () => runGeneration(p), params.accept);
}

function runGeneration(p: ResolvedParams): Board {
	const rng = p.rng;
	const { board, openArea } = generateBaseCave(p);
	board.darkness = 0.6;
	const openAreaSet = new Set(openArea.map((xy) => xy.toString()));
	const reservedPositions = new Set<string>();

	// --- River ---
	const riverArea: XY[] = p.generateRiver ? drawRiver(board, randomInt(2, 4, rng), 0.05, randomChance(0.5, rng), rng) : [];
	const riverSet = new Set(riverArea.map((xy) => xy.toString()));

	for (const xy of riverArea) {
		if (board.getThingsAt(xy).some((t) => t instanceof LevelChangerThing)) continue;
		removeAllThingsAt(board, xy);
		board.addThing(new WaterTile(xy));
	}

	// --- Entrance ---
	const previousLevelNum = p.previousLevelNumber ?? p.levelNumber - 1;
	let effectiveArrivalPoint: XY;

	if (p.generateSpecialEntrance) {
		const prefab = getCaveArrival(previousLevelNum);
		prefab.rotate(randomInt(0, 3, rng));

		const randomEmpty = getRandomEmptyCell(board, rng);
		if (!randomEmpty) throw new LevelGeneratorScrewUp("No empty cell for BFS start");

		const accessibleArea = breadthFirstSearch(
			randomEmpty,
			(xy) => xy.isInside(board) && board.isPassable(xy) && !riverSet.has(xy.toString()),
		);

		const prefabXYs = putPrefabOnArea(board, accessibleArea, prefab, rng);
		if (!prefabXYs) throw new LevelGeneratorScrewUp("No room for starting prefab.");

		for (const xy of prefabXYs) reservedPositions.add(xy.toString());

		const doorXY = prefabXYs.find((xy) => board.getThingsAt(xy).some((t) => t instanceof BlastDoor));
		if (!doorXY) throw new LevelGeneratorScrewUp("No blast door in arrival prefab.");

		const exitCandidate = doorXY.getCardinalNeighbors().find((xy) => !reservedPositions.has(xy.toString()));
		if (!exitCandidate || !exitCandidate.isInside(board)) {
			throw new LevelGeneratorScrewUp("Can't exit prefab.");
		}
		effectiveArrivalPoint = exitCandidate;

		const arrivalPoint = prefabXYs.find((xy) => board.getThingsAt(xy).some((t) => t instanceof Upstairs));
		if (!arrivalPoint) throw new LevelGeneratorScrewUp("No upstairs in arrival prefab.");
	} else {
		const possibleEntrances = openArea.filter((xy) => !xy.isInBorder(board));
		const entrance = getRandomEmptyCellInArea(possibleEntrances, board, rng);
		if (!entrance) throw new LevelGeneratorScrewUp("No empty cell for entrance.");
		effectiveArrivalPoint = entrance;
		board.addThing(p.entranceSupplier(effectiveArrivalPoint, previousLevelNum));
	}

	// --- Exit ---
	const distanceMapToArrival = DistanceMap.buildFrom(board, effectiveArrivalPoint, (xy) => board.isPassable(xy));

	let maxDistance = 0;
	distanceMapToArrival.forEach((_xy, distance) => {
		if (distance > maxDistance) maxDistance = distance;
	});

	if (maxDistance < p.minExitDistance) throw new LevelGeneratorScrewUp("Exit could not be placed far enough");

	// Second distance map: treat walls/rocks as passable (player can dig through them),
	// but chasms and lava remain impassable. This prevents the exit from being placed
	// close to the arrival point in a straight line separated only by diggable obstacles.
	const digDistanceMap = DistanceMap.buildFrom(board, effectiveArrivalPoint, (xy) => {
		if (!board.isInBounds(xy)) return false;
		return !board.getThingsAt(xy).some((t) => t instanceof Chasm || t instanceof LavaPool);
	});
	const minDigDistance = Math.max(Math.floor(p.minExitDistance / 5), 5);

	const minDistanceToExit = Math.floor(maxDistance * 0.9);
	const possibleExits: XY[] = [];
	distanceMapToArrival.forEach((xy, distance) => {
		if (
			distance >= minDistanceToExit &&
			!xy.isInBorder(board) &&
			!board.getThingsAt(xy).some((t) => t instanceof WaterTile)
		) {
			const digDistance = digDistanceMap.get(xy);
			if (digDistance === undefined || digDistance < minDigDistance) return;
			possibleExits.push(xy);
		}
	});

	if (possibleExits.length === 0) {
		throw new LevelGeneratorScrewUp("Could not find a suitable exit point");
	}

	const exitPoint = possibleExits[randomInt(0, possibleExits.length - 1, rng)];
	const distanceMapFromExit = DistanceMap.buildFrom(board, exitPoint, (xy) => board.isPassable(xy));
	const pathToExit = distanceMapFromExit.rollDownhill(effectiveArrivalPoint);
	const pathToExitSet = new Set(pathToExit.map((xy) => xy.toString()));

	if (p.generateExit) {
		board.addThing(p.exitSupplier(exitPoint, p.exitLevelNumber));
	}

	reservedPositions.add(effectiveArrivalPoint.toString());
	reservedPositions.add(exitPoint.toString());

	// --- Chasms ---
	const preChasmsArea = getSimplexNoiseArea(board, p.chasmFrequency, p.chasmLimit, rng)
		.filter((xy) => openAreaSet.has(xy.toString()))
		.filter((xy) => xy.distanceTo(effectiveArrivalPoint) > 6)
		.filter((xy) => xy.distanceTo(exitPoint) > 6)
		.filter((xy) => !riverSet.has(xy.toString()) && xy.getNeighbors().every((n) => !riverSet.has(n.toString())))
		.filter((xy) => !reservedPositions.has(xy.toString()));

	const chasmsArea = p.generateChasms ? removeLoneCoords(preChasmsArea) : [];
	const chasmsSet = new Set(chasmsArea.map((xy) => xy.toString()));

	for (const xy of chasmsArea) {
		const chasm = new Chasm(xy);
		board.addThing(chasm);
		if (pathToExitSet.has(xy.toString()) || pathToExitSet.has(xy.add(0, -1).toString())) {
			board.addThing(new MetalGridFloor(xy));
			chasm.setPassable(true);
		}
	}

	// --- Lava ---
	const preLavaArea = getSimplexNoiseArea(board, p.lavaFrequency, p.lavaLimit, rng)
		.filter((xy) => openAreaSet.has(xy.toString()))
		.filter((xy) => xy.distanceTo(effectiveArrivalPoint) > 6)
		.filter((xy) => xy.distanceTo(exitPoint) > 6)
		.filter((xy) => !riverSet.has(xy.toString()) && xy.getNeighbors().every((n) => !riverSet.has(n.toString())))
		.filter((xy) => !chasmsSet.has(xy.toString()) && xy.getNeighbors().every((n) => !chasmsSet.has(n.toString())))
		.filter((xy) => !reservedPositions.has(xy.toString()));

	const lavaArea = p.generateLavaPools ? removeLoneCoords(preLavaArea) : [];
	const lavaSet = new Set(lavaArea.map((xy) => xy.toString()));

	for (const xy of lavaArea) {
		const lavaPool = new LavaPool(xy);
		board.addThing(lavaPool);
		if (pathToExitSet.has(xy.toString()) || pathToExitSet.has(xy.add(0, -1).toString())) {
			board.addThing(new MetalGridFloor(xy));
			lavaPool.setPassable(true);
		}
	}

	// --- Area classification ---
	const finalDistanceMap = DistanceMap.buildFrom(board, effectiveArrivalPoint, (xy) => board.isPassable(xy));

	const diggableDistanceMap = DistanceMap.buildFrom(
		board,
		effectiveArrivalPoint,
		(xy) => board.isPassable(xy) || !openAreaSet.has(xy.toString()),
	);

	const unreachableAreas = openArea.filter((xy) => board.isPassable(xy)).filter((xy) => !diggableDistanceMap.has(xy));
	const unreachableSet = new Set(unreachableAreas.map((xy) => xy.toString()));

	const hardToReachAreas = openArea
		.filter((xy) => board.isPassable(xy))
		.filter((xy) => !finalDistanceMap.has(xy))
		.filter((xy) => !unreachableSet.has(xy.toString()));

	let openAreaAvailable = openArea
		.filter((xy) => (finalDistanceMap.get(xy) ?? Infinity) > p.mobSpawnMinDistanceFromArrival)
		.filter((xy) => (distanceMapFromExit.get(xy) ?? Infinity) > MOB_SPAWN_MIN_DISTANCE_FROM_EXIT)
		.filter((xy) => !pathToExitSet.has(xy.toString()))
		.filter((xy) => !riverSet.has(xy.toString()))
		.filter((xy) => !chasmsSet.has(xy.toString()))
		.filter((xy) => !lavaSet.has(xy.toString()))
		.filter((xy) => !unreachableSet.has(xy.toString()));

	// --- Prefab placement ---
	let areaForPrefabs = openAreaAvailable.filter((xy) => board.isCellEmpty(xy)).filter((xy) => !xy.isInBorder(board));

	const mustPlacePrefabs: Prefab[] = [...p.requiredPrefabs];
	for (let i = 0; i < p.numberOfBigContainers; i++) {
		mustPlacePrefabs.push(getBigContainerPrefab());
	}

	for (const prefab of mustPlacePrefabs) {
		prefab.rotate(randomInt(0, 3, rng));
		const prefabXYs = putPrefabOnArea(board, areaForPrefabs, prefab, rng);
		if (!prefabXYs) throw new LevelGeneratorScrewUp("Could not place required prefab.");
		const placedSet = new Set(prefabXYs.map((xy) => xy.toString()));
		areaForPrefabs = areaForPrefabs.filter((xy) => !placedSet.has(xy.toString()));
		openAreaAvailable = openAreaAvailable.filter((xy) => !placedSet.has(xy.toString()));
	}

	for (let i = 0; i < p.numberOfFlavourPrefabs; i++) {
		const prefab = getRandomFlavourPrefab(rng);
		prefab.rotate(randomInt(0, 3, rng));
		const prefabXYs = putPrefabOnArea(board, areaForPrefabs, prefab, rng);
		if (prefabXYs) {
			const placedSet = new Set(prefabXYs.map((xy) => xy.toString()));
			areaForPrefabs = areaForPrefabs.filter((xy) => !placedSet.has(xy.toString()));
			openAreaAvailable = openAreaAvailable.filter((xy) => !placedSet.has(xy.toString()));
		}
	}

	// --- Portal spawning ---
	const hardToReachSet = new Set(hardToReachAreas.map((xy) => xy.toString()));
	let easyToReachAreas = openAreaAvailable.filter((xy) => !hardToReachSet.has(xy.toString()));

	for (let i = 0; i < p.numberOfPortals; i++) {
		const isReinforced = randomChance(p.reinforcedPortalChance, rng);
		const portal = placeRandomPortal(easyToReachAreas, board, rng, isReinforced);
		if (portal) {
			if (p.portalSpawnProbability !== null) portal.setSpawnProbability(p.portalSpawnProbability);
			const portalArea = [portal.xy, ...portal.xy.getNeighbors()];
			const portalAreaSet = new Set(portalArea.map((xy) => xy.toString()));
			openAreaAvailable = openAreaAvailable.filter((xy) => !portalAreaSet.has(xy.toString()));
			easyToReachAreas = easyToReachAreas.filter((xy) => !portalAreaSet.has(xy.toString()));

			if (randomChance(p.slugantPortalChance, rng)) {
				portal.addConfig({ mobType: Slugant.key, share: 1 });
			} else if (p.mobSpawnerConfig) {
				portal.setMobSpawnerConfig(p.mobSpawnerConfig);
			}
		}
	}

	// --- Mob spawning ---
	const lootsAndMonstersArea = openAreaAvailable
		.filter((xy) => !hardToReachSet.has(xy.toString()))
		.filter((xy) => board.isPassable(xy));
	const lootsAndMonstersSet = new Set(lootsAndMonstersArea.map((xy) => xy.toString()));

	const canSpawnMobHere = (xy: XY): boolean => lootsAndMonstersSet.has(xy.toString()) && !board.hasMob(xy);

	spawnMobsWithNoise(board, p.mobGenerationConfig, rng, canSpawnMobHere, 400);

	// --- Multi-tile mobs ---
	for (let i = 0; i < p.numberOfBigSquids; i++) {
		for (let attempt = 0; attempt < 100; attempt++) {
			const xy = getRandomXYByCriteriaInArea(board.getAllXYs(), canSpawnMobHere, rng);
			if (!xy) break;
			if ((finalDistanceMap.get(xy) ?? Infinity) < 6) continue;
			if (BigSquid.create(board, xy)) break;
		}
	}

	for (let i = 0; i < p.numberOfArtilleryRobots; i++) {
		placeNxNMultiTileThing(() => new ArtilleryRobot(), board, 2, rng, canSpawnMobHere);
	}

	for (let i = 0; i < p.numberOfDrillerRobots; i++) {
		placeNxNMultiTileThing(() => new MiningRobot(), board, 2, rng, canSpawnMobHere);
	}

	// --- Loot placement ---
	const canSpawnLootHere = canSpawnMobHere;

	spawnThings(board, () => new LootBox(new XY(0, 0)), p.numberOfLootBoxes, rng, canSpawnLootHere);
	spawnThings(board, () => new MetalLootBox(new XY(0, 0)), p.numberOfMetalLootBoxes, rng, canSpawnLootHere);
	spawnThings(board, () => new MedicalLootBox(new XY(0, 0)), p.numberOfMedikits, rng, canSpawnLootHere);
	spawnThings(board, () => new KeyLootBox(new XY(0, 0)), p.numberOfKeys, rng, canSpawnLootHere);

	// Redistribute loot to hard-to-reach areas
	if (hardToReachAreas.length > 8) {
		const lootBoxesOnBoard = board.getPhysicalThings().filter((t): t is LootBox => t instanceof LootBox);

		for (const lootBox of lootBoxesOnBoard) {
			if (reservedPositions.has(lootBox.xy.toString())) continue;
			if (!randomChance(p.probabilityToPlaceLootInHardToReachAreas, rng)) continue;

			const target = getRandomXYByCriteriaInArea(
				hardToReachAreas,
				(xy) => board.isPassable(xy) && !board.getThingsAt(xy).some((t) => t instanceof LootBox),
				rng,
			);
			if (target) {
				board.moveThing(lootBox, target);
			}
		}
	}

	populateLootBoxes(board, p.lootTable, p.metalLootTable, p.rewardLevel, rng);
	if (p.vendingTable) {
		populateVendingMachines(board, p.vendingTable, rng);
	}
	assignGoldToPrePlacedMobs(board);
	board.dynamicGoldBudget = 200;

	return board;
}

function getRandomFlavourPrefab(rng: () => number): Prefab {
	const suppliers = [getSmallBloodTemple, getLTunnel, getLTunnel2, getLavaCross, getFireTowerChasm, getChasmLoot, getSculptures3];
	return suppliers[randomInt(0, suppliers.length - 1, rng)]();
}

function generateBaseCave(p: ResolvedParams): { board: Board; openArea: XY[] } {
	const rng = p.rng;
	let board!: Board;
	let openArea: XY[] = [];
	let shouldRetry = true;
	let attempts = 0;
	const maxAttempts = 50;
	let randomWallPercentage = p.randomStartingWallPercentage;

	while (shouldRetry) {
		attempts++;

		const baseBoard = new Board(BOARD_WIDTH, BOARD_HEIGHT, rng);
		baseBoard.defaultFloor = p.defaultFloor;

		// Generate random wall template
		const template = new Map<string, boolean>();
		for (let x = 0; x < BOARD_WIDTH; x++) {
			for (let y = 0; y < BOARD_HEIGHT; y++) {
				template.set(`${x},${y}`, rng() < randomWallPercentage);
			}
		}

		// Apply template to board
		for (let x = 0; x < BOARD_WIDTH; x++) {
			for (let y = 0; y < BOARD_HEIGHT; y++) {
				const xy = new XY(x, y);
				if (template.get(`${x},${y}`) || isAtBorder(baseBoard, xy)) {
					const wall = p.wallSupplier();
					wall.xy = xy;
					baseBoard.addThing(wall);
				}
			}
		}

		// Cellular automata smoothing
		for (let i = 0; i < p.smoothingIterationsType1; i++) {
			caveIterationType1(baseBoard, p.wallSupplier, rng);
		}
		for (let i = 0; i < p.smoothingIterationsType2; i++) {
			caveIterationType2(baseBoard, p.wallSupplier, rng);
		}

		// Find connected open area
		const randomEmptyCell = getRandomEmptyCell(baseBoard, rng);
		if (!randomEmptyCell) {
			if (attempts > maxAttempts) break;
			randomWallPercentage -= 0.01;
			continue;
		}

		openArea = breadthFirstSearch(randomEmptyCell, (pos) => pos.isInside(baseBoard) && baseBoard.isPassable(pos));

		const openAreaPercent = openArea.length / baseBoard.getAllXYs().length;

		shouldRetry = openAreaPercent < p.requiredOpenAreaPercent.min || openAreaPercent > p.requiredOpenAreaPercent.max;

		if (shouldRetry) {
			if (openAreaPercent > p.requiredOpenAreaPercent.max) {
				randomWallPercentage += 0.01;
			} else {
				randomWallPercentage -= 0.01;
			}
		} else {
			// Check centroid is near board center
			let sumX = 0;
			let sumY = 0;
			for (const xy of openArea) {
				sumX += xy.x;
				sumY += xy.y;
			}
			const avgX = Math.floor(sumX / openArea.length);
			const avgY = Math.floor(sumY / openArea.length);
			const centroid = new XY(avgX, avgY);
			const boardCenter = new XY(Math.floor((baseBoard.width - 1) / 2), Math.floor((baseBoard.height - 1) / 2));
			shouldRetry = !centroid.isInSquareRadius(boardCenter, 8);
		}

		if (shouldRetry) {
			if (attempts > maxAttempts) {
				shouldRetry = false;
			}
		} else {
			// Fill isolated empty cells with walls
			const openAreaSet = new Set(openArea.map((xy) => xy.toString()));
			for (const xy of baseBoard.getAllXYs()) {
				if (baseBoard.isCellEmpty(xy) && !openAreaSet.has(xy.toString())) {
					const wall = p.wallSupplier();
					wall.xy = xy;
					baseBoard.addThing(wall);
				}
			}
		}

		board = baseBoard;
	}

	return { board, openArea };
}

function caveIterationType1(board: Board, wallSupplier: () => PhysicalThing, rng: () => number): void {
	const xys = shuffleArray(board.getAllXYs(), rng);

	for (const xy of xys) {
		const currentHasWall = hasWall(board, xy);
		let wallsAround = xy
			.getNeighbors()
			.filter((n) => n.isInside(board))
			.filter((n) => hasWall(board, n)).length;
		if (currentHasWall) wallsAround++;

		const wallsAroundRadius2 = board
			.getAllXYs()
			.filter((pos) => pos.isInSquareRadius(xy, 2))
			.filter((n) => hasWall(board, n)).length;

		const nowIsWall = wallsAround >= 5 || wallsAroundRadius2 <= 2;

		if (nowIsWall) {
			if (!currentHasWall) {
				const wall = wallSupplier();
				wall.xy = xy;
				board.addThing(wall);
			}
		} else {
			if (currentHasWall) removeAllThingsAt(board, xy);
		}
	}
}

function caveIterationType2(board: Board, wallSupplier: () => PhysicalThing, rng: () => number): void {
	const xys = shuffleArray(board.getAllXYs(), rng);

	for (const xy of xys) {
		const currentHasWall = hasWall(board, xy);
		let wallsAround = xy
			.getNeighbors()
			.filter((n) => n.isInside(board))
			.filter((n) => hasWall(board, n)).length;
		if (currentHasWall) wallsAround++;

		const nowIsWall = wallsAround >= 5;

		if (nowIsWall) {
			if (!currentHasWall) {
				const wall = wallSupplier();
				wall.xy = xy;
				board.addThing(wall);
			}
		} else {
			if (currentHasWall) removeAllThingsAt(board, xy);
		}
	}
}

function drawRiver(
	board: Board,
	initialWidth: number,
	widthVariationChance: number,
	vertical: boolean,
	rng: () => number,
): XY[] {
	const minWidth = 2;
	const riverPositions: XY[] = [];
	let width = initialWidth;
	let y = randomInt(0, board.height - 1, rng);
	let currentDirection = randomInt(-1, 1, rng);
	const maxX = vertical ? board.height - 1 : board.width - 1;

	for (let x = 0; x <= maxX; x++) {
		for (let riverY = y - Math.floor(width / 2); riverY <= y + Math.floor(width / 2); riverY++) {
			const riverXY = vertical ? new XY(riverY, x) : new XY(x, riverY);
			if (!riverXY.isInside(board)) continue;
			riverPositions.push(riverXY);
		}
		y += currentDirection;

		const widthRoll = rng();
		if (widthRoll < widthVariationChance / 2) {
			width = Math.max(minWidth, width - 1);
		} else if (widthRoll < widthVariationChance) {
			width++;
		}

		currentDirection = markovRiverDirection(currentDirection, rng);
	}

	return riverPositions;
}

function markovRiverDirection(current: number, rng: () => number): number {
	const roll = rng() * 100;
	if (roll < 70) return current;
	if (current === -1) return roll < 85 ? 0 : 1;
	if (current === 0) return roll < 85 ? -1 : 1;
	return roll < 85 ? 0 : -1;
}

function removeLoneCoords(xys: XY[]): XY[] {
	const xySet = new Set(xys.map((xy) => xy.toString()));
	return xys.filter((xy) => xy.getCardinalNeighbors().some((n) => xySet.has(n.toString())));
}

function hasWall(board: Board, xy: XY): boolean {
	return !board.isPassable(xy);
}

function isAtBorder(board: Board, xy: XY): boolean {
	return xy.x === 0 || xy.y === 0 || xy.x === board.width - 1 || xy.y === board.height - 1;
}
